import { useEffect, useState } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Gear, FloppyDisk, Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const fmtMilhar = (n) =>
  n != null && n !== "" ? Number(n).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";

const fmt4 = (n) => (n != null && n !== "" ? Number(n).toFixed(4).replace(".", ",") : "—");

// Duas faixas, espelhando as faixas de preço da regra vigente. As colunas
// adicional_ate_60/acima_60 continuam no banco como legado e não são editadas
// aqui: períodos novos nascem com 0,0 nelas (default do backend) e períodos
// antigos preservam o que já tinham, porque abrirModalEditar espalha o objeto
// inteiro vindo da API.
const PERIODO_VAZIO = {
  nome: "",
  hora_inicio: "",
  hora_fim: "",
  adicional_ate_5: 0,
  adicional_acima_5: 0,
  ativo: true,
};

export default function Admin() {
  const [params, setParams] = useState(null);
  const [saving, setSaving] = useState(false);
  const [limiteVendedorStr, setLimiteVendedorStr] = useState("");
  const [limiteGlobalStr, setLimiteGlobalStr] = useState("");

  const [periodos, setPeriodos] = useState([]);
  const [modalAberto, setModalAberto] = useState(false);
  const [periodoEditando, setPeriodoEditando] = useState(null);
  const [formPeriodo, setFormPeriodo] = useState(PERIODO_VAZIO);
  const [salvandoPeriodo, setSalvandoPeriodo] = useState(false);

  useEffect(() => {
    api.get("/parametros").then((r) => {
      setParams(r.data);
      setLimiteVendedorStr(fmtMilhar(r.data.limite_vendedor_usd));
      setLimiteGlobalStr(fmtMilhar(r.data.limite_global_usd));
    });
    api.get("/parametros/periodos").then((r) => setPeriodos(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/parametros", params);
      toast.success("Parâmetros atualizados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  const abrirModalNovo = () => {
    setPeriodoEditando(null);
    setFormPeriodo(PERIODO_VAZIO);
    setModalAberto(true);
  };

  const abrirModalEditar = (p) => {
    setPeriodoEditando(p);
    setFormPeriodo({ ...p });
    setModalAberto(true);
  };

  const salvarPeriodo = async () => {
    if (!formPeriodo.nome.trim() || !formPeriodo.hora_inicio || !formPeriodo.hora_fim) {
      toast.error("Nome e horários são obrigatórios");
      return;
    }
    setSalvandoPeriodo(true);
    try {
      if (periodoEditando) {
        const r = await api.put(`/parametros/periodos/${periodoEditando.id}`, formPeriodo);
        setPeriodos((prev) => prev.map((p) => (p.id === periodoEditando.id ? r.data : p)));
        toast.success("Período atualizado");
      } else {
        const r = await api.post("/parametros/periodos", formPeriodo);
        setPeriodos((prev) => [...prev, r.data]);
        toast.success("Período criado");
      }
      setModalAberto(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally {
      setSalvandoPeriodo(false);
    }
  };

  const removerPeriodo = async (id) => {
    try {
      await api.delete(`/parametros/periodos/${id}`);
      setPeriodos((prev) => prev.filter((p) => p.id !== id));
      toast.success("Período removido");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const setForm = (k, v) => setFormPeriodo((f) => ({ ...f, [k]: v }));

  if (!params) return <Layout><div className="p-8 text-[#6b89a8]">Carregando...</div></Layout>;

  const num = (k) => ({ value: params[k] ?? "", onChange: (e) => setParams({ ...params, [k]: parseFloat(e.target.value) || 0 }) });
  const intN = (k) => ({ value: params[k] ?? "", onChange: (e) => setParams({ ...params, [k]: parseInt(e.target.value) || 0 }) });
  const str = (k) => ({ value: params[k] ?? "", onChange: (e) => setParams({ ...params, [k]: e.target.value }) });

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-4xl space-y-6">
        <div>
          <p className="text-[11px] tracking-[0.25em] text-[#6b89a8] uppercase">Administrativo</p>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Gear weight="duotone" className="w-8 h-8 text-[#40B6A2]" />
            Parametrização do Sistema
          </h1>
        </div>

        <Section title="Precificação">
          <Field
            label="Spread até o corte (R$)"
            testid="param-spread-5"
            help="Valor fixo em reais, somado ao spot nas operações dentro do prazo de corte."
          >
            <Input type="number" step="0.0001" className="bg-transparent border-[#123a5e] font-mono-data" {...num("spread_ate_5")} />
          </Field>
          <Field
            label="Spread acima do corte (% do spot)"
            testid="param-spread-pct"
            help={`Decimal — ex: 0.007 = 0,7% do spot. Não varia com o prazo: 6 dias e 300 dias recebem o mesmo percentual.${
              params.spread_pct_acima_5 ? ` Hoje: ${(params.spread_pct_acima_5 * 100).toLocaleString("pt-BR", { maximumFractionDigits: 4 })}%.` : ""
            }`}
          >
            <Input type="number" step="0.0001" className="bg-transparent border-[#123a5e] font-mono-data" {...num("spread_pct_acima_5")} />
          </Field>
          <Field
            label="Prazo de corte (dias)"
            testid="param-prazo-corte"
            help="Até este prazo vale o spread fixo; acima dele, o percentual do spot."
          >
            <Input type="number" className="bg-transparent border-[#123a5e] font-mono-data" {...intN("spread_prazo_corte")} />
          </Field>
        </Section>

        {/* Spreads por Período */}
        <div className="border border-[#123a5e] rounded-sm p-6 bg-[#0B2D4D] space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm tracking-[0.2em] uppercase text-[#8aa9c9]">Spreads por Período</h2>
            <Button onClick={abrirModalNovo} size="sm" className="bg-[#40B6A2] text-black hover:bg-[#359a89] font-semibold">
              <Plus weight="bold" className="w-4 h-4 mr-1" />
              Adicionar Período
            </Button>
          </div>

          {periodos.length === 0 ? (
            <p className="text-[#6b89a8] text-sm">
              Nenhum período configurado. Os spreads base serão aplicados em todos os horários.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#6b89a8] text-xs uppercase tracking-wider border-b border-[#123a5e]">
                    <th className="text-left pb-2 pr-4">Nome</th>
                    <th className="text-left pb-2 pr-4">Horário</th>
                    <th className="text-right pb-2 pr-4">Adic. ≤ corte</th>
                    <th className="text-right pb-2 pr-4">Adic. &gt; corte</th>
                    <th className="text-center pb-2 pr-4">Status</th>
                    <th className="pb-2 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {periodos.map((p) => (
                    <tr key={p.id} className="border-b border-[#0d3358] last:border-0">
                      <td className="py-2 pr-4 text-white">{p.nome}</td>
                      <td className="py-2 pr-4 font-mono-data text-[#8aa9c9]">{p.hora_inicio} – {p.hora_fim}</td>
                      <td className="py-2 pr-4 text-right font-mono-data text-white">{Number(p.adicional_ate_5).toFixed(4)}</td>
                      <td className="py-2 pr-4 text-right font-mono-data text-white">{Number(p.adicional_acima_5).toFixed(4)}</td>
                      <td className="py-2 pr-4 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${p.ativo ? "bg-emerald-900/50 text-emerald-400" : "bg-[#0d2a40] text-[#6b89a8]"}`}>
                          {p.ativo ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="py-2">
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => abrirModalEditar(p)} className="text-[#6b89a8] hover:text-white transition-colors" title="Editar">
                            <PencilSimple weight="bold" className="w-4 h-4" />
                          </button>
                          <button onClick={() => removerPeriodo(p.id)} className="text-[#6b89a8] hover:text-red-400 transition-colors" title="Remover">
                            <Trash weight="bold" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Section title="Prazos & Tolerâncias">
          <Field label="Prazo máx. sem vinculação (dias)" testid="param-prazo"><Input type="number" className="bg-transparent border-[#123a5e] font-mono-data" {...intN("prazo_maximo_sem_vinculacao")} /></Field>
          <Field label="Tolerância volume pedido (%)" testid="param-tol-volume"><Input type="number" step="0.1" className="bg-transparent border-[#123a5e] font-mono-data" {...num("tolerancia_volume_pct")} /></Field>
        </Section>

        <Section title="Horário de Operação">
          <Field label="Início (HH:MM)" testid="param-ini"><Input type="time" className="bg-transparent border-[#123a5e] font-mono-data" {...str("horario_inicio")} /></Field>
          <Field label="Fim (HH:MM)" testid="param-fim"><Input type="time" className="bg-transparent border-[#123a5e] font-mono-data" {...str("horario_fim")} /></Field>
        </Section>

        <Section title="Limites Operacionais">
          <Field label="Limite de exposição por vendedor (USD)" testid="param-limite-vendedor" help="Limite global aplicado igualmente a todos os vendedores">
            <Input type="text" inputMode="numeric" className="bg-transparent border-[#123a5e] font-mono-data"
              value={limiteVendedorStr}
              onChange={(e) => setLimiteVendedorStr(e.target.value)}
              onBlur={() => {
                const raw = limiteVendedorStr.replace(/\./g, "").replace(",", ".").replace(/[^\d.]/g, "");
                const val = parseFloat(raw) || 0;
                setParams((p) => ({ ...p, limite_vendedor_usd: val }));
                setLimiteVendedorStr(fmtMilhar(val));
              }}
            />
          </Field>
          <Field label="Limite global do sistema (USD)" testid="param-limite" help="Exposição máxima total do sistema">
            <Input type="text" inputMode="numeric" className="bg-transparent border-[#123a5e] font-mono-data"
              value={limiteGlobalStr}
              onChange={(e) => setLimiteGlobalStr(e.target.value)}
              onBlur={() => {
                const raw = limiteGlobalStr.replace(/\./g, "").replace(",", ".").replace(/[^\d.]/g, "");
                const val = parseFloat(raw) || 0;
                setParams((p) => ({ ...p, limite_global_usd: val }));
                setLimiteGlobalStr(fmtMilhar(val));
              }}
            />
          </Field>
        </Section>

        {/* Parâmetros da regra anterior. Não entram em nenhuma operação nova
            desde a regra de set/2026, mas precificaram tudo o que está no
            histórico — ficam visíveis, e só isso, para auditoria dos
            relatórios de datas passadas. */}
        <div className="border border-dashed border-[#123a5e] rounded-sm p-6 bg-[#0B2D4D]/40 space-y-4" data-testid="secao-legado">
          <div className="space-y-1">
            <h2 className="text-sm tracking-[0.2em] uppercase text-[#6b89a8]">Legado — não usado em novas operações</h2>
            <p className="text-[11px] text-[#6b89a8] leading-tight">
              Faixas de 60 dias e juros compostos da regra anterior. Somente leitura: precificaram o histórico e não afetam nenhuma cotação nova.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Spread até 60 dias (R$)" testid="param-spread-60">
              <Input
                readOnly
                tabIndex={-1}
                value={fmt4(params.spread_ate_60)}
                className="bg-transparent border-[#123a5e]/60 font-mono-data text-[#6b89a8] focus-visible:ring-0 cursor-default"
              />
            </Field>
            <Field label="Spread acima de 60 dias (R$)" testid="param-spread-acima">
              <Input
                readOnly
                tabIndex={-1}
                value={fmt4(params.spread_acima_60)}
                className="bg-transparent border-[#123a5e]/60 font-mono-data text-[#6b89a8] focus-visible:ring-0 cursor-default"
              />
            </Field>
            <Field label="Taxa juros a.m. (decimal)" testid="param-juros">
              <Input
                readOnly
                tabIndex={-1}
                value={fmt4(params.taxa_juros_mensal)}
                className="bg-transparent border-[#123a5e]/60 font-mono-data text-[#6b89a8] focus-visible:ring-0 cursor-default"
              />
            </Field>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="btn-salvar-parametros" className="bg-[#40B6A2] text-black hover:bg-[#359a89] font-semibold">
            <FloppyDisk weight="bold" className="w-4 h-4 mr-2" />
            {saving ? "Salvando..." : "Salvar parâmetros"}
          </Button>
        </div>
      </div>

      {/* Modal Período */}
      {modalAberto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setModalAberto(false)}>
          <div className="bg-[#0B2D4D] border border-[#123a5e] rounded-sm p-6 w-full max-w-md space-y-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm tracking-[0.2em] uppercase text-[#8aa9c9]">
              {periodoEditando ? "Editar Período" : "Novo Período"}
            </h3>

            <Field label="Nome">
              <Input
                className="bg-transparent border-[#123a5e]"
                placeholder="ex: Período Manhã"
                value={formPeriodo.nome}
                onChange={(e) => setForm("nome", e.target.value)}
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Hora início">
                <Input type="time" className="bg-transparent border-[#123a5e] font-mono-data" value={formPeriodo.hora_inicio} onChange={(e) => setForm("hora_inicio", e.target.value)} />
              </Field>
              <Field label="Hora fim">
                <Input type="time" className="bg-transparent border-[#123a5e] font-mono-data" value={formPeriodo.hora_fim} onChange={(e) => setForm("hora_fim", e.target.value)} />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Adicional ≤ corte">
                <Input type="number" step="0.0001" className="bg-transparent border-[#123a5e] font-mono-data" value={formPeriodo.adicional_ate_5} onChange={(e) => setForm("adicional_ate_5", parseFloat(e.target.value) || 0)} />
              </Field>
              <Field label="Adicional > corte">
                <Input type="number" step="0.0001" className="bg-transparent border-[#123a5e] font-mono-data" value={formPeriodo.adicional_acima_5 ?? 0} onChange={(e) => setForm("adicional_acima_5", parseFloat(e.target.value) || 0)} />
              </Field>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setForm("ativo", !formPeriodo.ativo)}
                className={`relative w-10 h-6 rounded-full transition-colors ${formPeriodo.ativo ? "bg-[#40B6A2]" : "bg-[#123a5e]"}`}
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${formPeriodo.ativo ? "translate-x-5" : "translate-x-1"}`} />
              </button>
              <Label className="text-xs uppercase tracking-wider text-[#8aa9c9]">
                {formPeriodo.ativo ? "Ativo" : "Inativo"}
              </Label>
            </div>

            <div className="flex justify-end gap-3 pt-1">
              <Button variant="outline" onClick={() => setModalAberto(false)} className="border-[#123a5e] text-[#8aa9c9] hover:bg-[#0d2a40]">
                Cancelar
              </Button>
              <Button onClick={salvarPeriodo} disabled={salvandoPeriodo} className="bg-[#40B6A2] text-black hover:bg-[#359a89] font-semibold">
                {salvandoPeriodo ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-[#123a5e] rounded-sm p-6 bg-[#0B2D4D] space-y-4">
      <h2 className="text-sm tracking-[0.2em] uppercase text-[#8aa9c9]">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children, testid, help }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      <Label className="text-xs uppercase tracking-wider text-[#8aa9c9]">{label}</Label>
      {children}
      {help && <p className="text-[11px] text-[#6b89a8] leading-tight">{help}</p>}
    </div>
  );
}
